import { differenceInDays, format, isAfter } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { Calendar, CalendarRange, CheckCircle2, CircleDollarSign, Crown, Star, UserRound } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Subscription } from "@/types/subscription";
import { SubscriptionRenewal } from "./SubscriptionRenewal";
import { getStatusBadgeStyle } from "@/components/clients/ClientUtils";
import { getStatusText } from "@/utils/clientUtils";
import { useCurrency } from "@/contexts/CurrencyContext";

interface SubscriptionCardProps {
  subscription: Subscription;
  payments: { amount: number }[];
  onRenew: () => void;
  isExpiringSoon: boolean;
  /**
   * Planning(s) que cet abonnement ouvre. `null`/absent = la salle n'utilise
   * pas le multi-planning : on n'affiche rien, l'écran reste celui d'avant.
   */
  calendarLabel?: string | null;
  featured?: boolean;
}

export function SubscriptionCard({
  subscription,
  payments,
  onRenew,
  isExpiringSoon,
  calendarLabel = null,
  featured = false,
}: SubscriptionCardProps) {
  const { t, i18n } = useTranslation(["subscriptions", "common"]);
  const dateLocale = i18n.language === "fr" ? fr : enUS;
  const { formatCurrency } = useCurrency();

  const totalPaid = payments?.reduce((acc, p) => acc + (p.amount ?? 0), 0) ?? 0;
  const totalPrice = subscription.price ?? 0;
  const remainingBalance = totalPrice - totalPaid;

  const today = new Date();
  const isExpiredByDate = isAfter(today, new Date(subscription.end_date));
  const isExpiredBySessions = (subscription.sessions_remaining ?? 0) <= 0;
  const isExpired = isExpiredByDate || isExpiredBySessions;

  const displayStatus = isExpired ? "Expired" : subscription.status;

  if (featured) {
    const planTitle = t(`plans.${subscription.type.toLowerCase()}.title`, {
      ns: "subscriptions",
      defaultValue: subscription.type,
    });

    return (
      <section className="athlete-subscription-plan overflow-hidden border border-primary/70 bg-card/95 p-4 shadow-lg md:p-6">
        <div className="mb-5">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="text-2xl font-bold md:text-3xl">{t("currentPlan", { ns: "subscriptions" })}</h2>
            <span className="inline-flex items-center gap-2 rounded-full border border-primary/70 bg-primary/10 px-3 py-1 text-sm font-semibold text-primary">
              <Crown className="h-4 w-4" />
              {planTitle}
            </span>
          </div>
          <p className="mt-2 text-sm text-muted-foreground md:text-base">
            {t(`plans.${subscription.type.toLowerCase()}.description`, { ns: "subscriptions", defaultValue: subscription.type })}
          </p>
        </div>

        <div className="grid gap-3 lg:grid-cols-[1.15fr_0.85fr]">
          <div className="athlete-subscription-details border border-border/70 bg-background/55 p-4">
            <div className="space-y-4">
              <SubscriptionLine icon={UserRound} label={t("status")}>
                <span className="rounded-full bg-chart-2/15 px-3 py-1 text-xs font-semibold text-chart-2">
                  {t(getStatusText(displayStatus), { ns: "subscriptions" })}
                </span>
              </SubscriptionLine>
              <SubscriptionLine icon={Calendar} label={t("startDate")}>{format(new Date(subscription.start_date), "PPP", { locale: dateLocale })}</SubscriptionLine>
              <SubscriptionLine icon={Calendar} label={t("endDate")}>{format(new Date(subscription.end_date), "PPP", { locale: dateLocale })}</SubscriptionLine>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <div className="athlete-subscription-counter border border-primary/50 bg-primary/10 p-3 text-center">
                <Calendar className="mx-auto h-5 w-5 text-primary" />
                <p className="mt-2 text-xs text-muted-foreground">{t("sessions.remaining")}</p>
                <p className="mt-1 text-xl font-bold">{subscription.sessions_remaining} / {subscription.sessions_total}</p>
              </div>
              <div className="athlete-subscription-counter border border-primary/50 bg-primary/10 p-3 text-center">
                <Star className="mx-auto h-5 w-5 text-primary" />
                <p className="mt-2 text-xs text-muted-foreground">{t("jokers.remaining")}</p>
                <p className="mt-1 text-xl font-bold">{subscription.jokers_remaining} / {subscription.jokers_total}</p>
              </div>
            </div>

            <div className="mt-5 space-y-3">
              <SubscriptionLine icon={CircleDollarSign} label={t("amountTotal")}>{formatCurrency(totalPrice)}</SubscriptionLine>
              <SubscriptionLine icon={CircleDollarSign} label={t("amountPaid")}>{formatCurrency(totalPaid)}</SubscriptionLine>
              <SubscriptionLine icon={CircleDollarSign} label={t("amountRemaining")}>
                <span className={remainingBalance > 0 ? "text-destructive" : "text-chart-2"}>
                  {remainingBalance > 0 ? formatCurrency(remainingBalance) : t("fullyPaid")}
                </span>
              </SubscriptionLine>
            </div>
          </div>

          <div className="athlete-subscription-renewal flex flex-col justify-between border border-primary/35 bg-primary/10 p-5">
            <div>
              <h3 className="text-xl font-bold text-primary">{planTitle}</h3>
              <p className="mt-4 text-4xl font-bold text-primary">{formatCurrency(totalPrice)}</p>
              <div className="mt-6 space-y-3 text-sm">
                <p className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 shrink-0 text-primary" />{subscription.sessions_total} {t("sessions.total").toLowerCase()}</p>
                {calendarLabel && <p className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 shrink-0 text-primary" />{calendarLabel}</p>}
                <p className="flex items-center gap-2"><CheckCircle2 className="h-5 w-5 shrink-0 text-primary" />{format(new Date(subscription.end_date), "PPP", { locale: dateLocale })}</p>
              </div>
            </div>
            <SubscriptionRenewal subscription={subscription} onRenew={onRenew} isExpiringSoon={isExpiringSoon} featured />
          </div>
        </div>
      </section>
    );
  }

  return (
    <Card className="bg-card/60 backdrop-blur-xl border border-border/40 shadow-lg hover:shadow-xl transition-shadow">
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          <span>{t("currentPlan", { ns: "subscriptions" })}</span>
          {calendarLabel && (
            <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-medium text-primary">
              <CalendarRange className="h-3 w-3" />
              {calendarLabel}
            </span>
          )}
        </CardTitle>
        <CardDescription>
          {t(`plans.${subscription.type.toLowerCase()}.description`, {
            ns: "subscriptions",
            defaultValue: subscription.type
          })}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div className="flex justify-between">
              <p className="text-sm text-muted-foreground">{t("status")}</p>
              <p className="text-sm font-medium">
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                    getStatusBadgeStyle(displayStatus)
                  }`}
                >
                  {t(getStatusText(displayStatus), { ns: "subscriptions" })}
                </span>
              </p>
            </div>

            <div className="flex justify-between">
              <p className="text-sm text-muted-foreground">{t("startDate")}</p>
              <p className="text-sm font-medium">
                {format(new Date(subscription.start_date), "PPP", {
                  locale: dateLocale,
                })}
              </p>
            </div>

            <div className="flex justify-between">
              <p className="text-sm text-muted-foreground">{t("endDate")}</p>
              <p className="text-sm font-medium">
                {format(new Date(subscription.end_date), "PPP", {
                  locale: dateLocale,
                })}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="flex items-center bg-amber-500/10 backdrop-blur-sm border border-amber-500/20 p-3 rounded-xl">
                <Calendar className="h-5 w-5 mr-3 text-amber-500" />
                <div>
                  <p className="text-xs text-muted-foreground">
                    {t("sessions.remaining")}
                  </p>
                  <p
                    className={`text-xl font-bold ${
                      isExpiredBySessions ? "text-destructive" : ""
                    }`}
                  >
                    {subscription.sessions_remaining}/{subscription.sessions_total}
                  </p>
                </div>
              </div>

              <div className="flex items-center bg-purple-500/10 backdrop-blur-sm border border-purple-500/20 p-3 rounded-xl">
                <Star className="h-5 w-5 mr-3 text-purple-500" />
                <div>
                  <p className="text-xs text-muted-foreground">
                    {t("jokers.remaining")}
                  </p>
                  <p className="text-xl font-bold">
                    {subscription.jokers_remaining}/{subscription.jokers_total}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-2 mt-6">
              <div className="flex justify-between">
                <p className="text-sm text-muted-foreground">{t("amountTotal")}</p>
                <p className="text-sm font-medium">{totalPrice.toFixed(2)} DT</p>
              </div>
              <div className="flex justify-between">
                <p className="text-sm text-muted-foreground">{t("amountPaid")}</p>
                <p className="text-sm font-medium">{totalPaid.toFixed(2)} DT</p>
              </div>
              <div className="flex justify-between">
                <p className="text-sm text-muted-foreground">
                  {t("amountRemaining")}
                </p>
                <p
                  className={`text-sm font-medium ${
                    remainingBalance > 0 ? "text-destructive" : "text-green-600"
                  }`}
                >
                  {remainingBalance > 0
                    ? `${remainingBalance.toFixed(2)} DT`
                    : t("fullyPaid")}
                </p>
              </div>
            </div>
          </div>

          <SubscriptionRenewal
            subscription={subscription}
            onRenew={onRenew}
            isExpiringSoon={isExpiringSoon}
          />
        </div>
      </CardContent>
    </Card>
  );
}

function SubscriptionLine({ icon: Icon, label, children }: {
  icon: typeof Calendar;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[auto_1fr_auto] items-center gap-3 text-sm">
      <Icon className="h-5 w-5 text-muted-foreground" />
      <span className="text-muted-foreground">{label}</span>
      <span className="text-right font-semibold">{children}</span>
    </div>
  );
}
